import { configCaas, configLatestStory, configTeaser } from "@pub/config";
import placeholders from "@pub/placeholders";
import { LatestArticle } from "@sphtech/dmg-design-system";
import {
  TeaserData,
  TeaserDataApiResponse,
} from "@src/app/types/ServerCustomContext";
import useSWR from "swr";

import { ResolvedCUEElement } from "../elements";
import styles from "./filteredStoryWidget.module.css";

function filteredListingParseJSON(
  value: string | null,
): { tagID?: string; tagName?: string } | null {
  if (!value) {
    return null;
  }

  try {
    return JSON.parse(value) as { tagID?: string; tagName?: string };
  } catch (error) {
    console.error("Error parsing JSON", { value }, error);
    return null;
  }
}

type FilteredListingWidgetProps = {
  children: ResolvedCUEElement[];
};

const FilteredListingLoaded = (tag: string, type: string) => {
  let storyTypes =
    "blogStory,featureStory,galleryStory,reviewStory,techNews,videoStory";
  let limit = 15;
  if (type === "articles") {
    storyTypes = "blogStory,featureStory,galleryStory,reviewStory";
    limit = 8;
  }
  if (type === "news") {
    storyTypes = "techNews";
    limit = 8;
  }
  if (type === "products") {
    storyTypes = "productsStory";
    limit = 6;
  }
  const { data } = useSWR<TeaserDataApiResponse>(
    `/_plat/api/teaser/latest?${[
      tag && `tags=${encodeURIComponent(tag)}`,
      storyTypes && `storyTypes=${encodeURIComponent(storyTypes)}`,
      `size=${limit}`,
    ]
      .filter(Boolean)
      .join("&")}`,
  );

  if (!data?.results) return null;

  return data.results;
};

type SectionType = {
  type: string;
  caption?: string;
  stories?: TeaserData[];
  url?: string;
};

const FilteredListingWidget = ({ children }: FilteredListingWidgetProps) => {
  const col1: SectionType[] = [];
  const col2: SectionType[] = [];
  children.forEach((child) => {
    //const stories = FilteredListingLoaded(child.additionalFields., "allarticles");
    if (child.type === "filtered_listing_info") {
      const filteredCondition = filteredListingParseJSON(
        child.additionalFields.filteredListingType?.value || null,
      );
      const tag = (filteredCondition?.tagID || "").replace(
        configCaas.tagIdPrefix,
        "",
      );
      const contentType =
        child.additionalFields.contentWidgetContentType?.value || "allarticles";
      const caption = child.additionalFields.contentWidgetCaption?.value;
      const filteredStories = FilteredListingLoaded(tag, contentType);
      const section = {
        type: contentType,
        caption: caption || "",
        stories: filteredStories || [],
        url: contentType === "news" ? `/tag/${tag}/news` : `/tag/${tag}`,
      };

      if (child.additionalFields.columnID?.value === 2) {
        col2.push(section);
      } else {
        col1.push(section);
      }
    }
  });
  return (
    <div className={styles.filteredStoryWidget}>
      {FilteredListingWidgetColumn(col1, styles.filteredStoryColumn || "")}
      {FilteredListingWidgetColumn(col2, styles.filteredStoryColumn || "")}
    </div>
  );
};

const FilteredListingWidgetColumn = (
  column: SectionType[],
  classname: string,
) => {
  if (column.length < 1) {
    return null;
  }
  return (
    <div className={classname}>
      {column.map(
        (section, index) =>
          section.stories &&
          section.stories.length > 0 && (
            <div key={index}>
              <h3 className={styles.filterStorySectionName}>
                {section.caption}
              </h3>
              {section.stories.map((teaser, index) =>
                section.type === "news" ? (
                  <a
                    key={index}
                    href={teaser.path}
                    className={styles.filteredStoryNews}
                  >
                    {teaser.title}
                  </a>
                ) : (
                  <LatestArticle
                    key={index}
                    {...teaser}
                    mode="landscape"
                    portraitImageWidth={configLatestStory.portraitImageWidth}
                    landscapeImageWidth={configLatestStory.landscapeImageWidth}
                    placeholders={placeholders}
                    displayPublishDate={configTeaser.publishDate}
                    mobileMode={configTeaser.mobileMode}
                    CustomHeadingElement="h2"
                    dateAfter={configTeaser.dateAfter}
                  />
                ),
              )}
              {section.url && (
                <div className={styles.filteredStoryViewMore}>
                  <a href={section.url}>&raquo; View more</a>
                </div>
              )}
            </div>
          ),
      )}
    </div>
  );
};

export default FilteredListingWidget;
